import { find, get, noop } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { grey, navy } from '@zocdoc/branding/colors';
import { CollapsibleTextByLine } from '@zocdoc/components/src/CollapsibleTextByLine';
import { breakpoint, Breakpoints } from '@zocdoc/components/src/grid';
import TrackingPixel from '@zocdoc/components/src/TrackingPixel';
import Lazyload from '@zocdoc/components/src/LazyLoad';
import Stars from '@zocdoc/components/src/stars';
import { FontFamily } from '@zocdoc/components/src/typography';

import {
    INFO_TEXT_FONT_SIZE,
    INFO_TEXT_LINE_HEIGHT,
    PROVIDER_RESULT_TYPES,
    STARS_BASE_SIZE,
    STARS_MEDIUM_SIZE,
    TRACKING_ACTIONS,
    TRACKING_CONSTANTS,
} from '../constants';
import { LOCALIZED_STRINGS } from './constants';
import { isCommunity } from '../../../../../Shared/utils/communityLabel';
import { titlesFrom } from '../../../../../Shared/utils/institutesAndTitles';
import SponsoredBanner from './SponsoredBanner';
import { DoctorPhoto, PhotoContainer } from '../DoctorPhoto/DoctorPhoto';
import {
    DoctorInfoContainer,
    DoctorInfoTopBar,
    DoctorName,
    DoctorSpecialty,
    DoctorCardContainer,
} from '../primitives';

import cookie from 'js-cookie';
import { sanitizeId } from '../../../../../config/ab/util';

// caching the array to help prevent memory pressure
const FALLBACK_ARRAY = [];
const getTier = data => get(find(get(data, 'provider.selectedInsurance.properties', FALLBACK_ARRAY), { key: 'tier' }), 'value');

export class BaseDoctorCard extends React.PureComponent {
    static propTypes = {
        /**
         * From GQL withSelectedInsurance()
         */
        data: PropTypes.shape({
            provider: PropTypes.shape({
                selectedInsurance: PropTypes.shape({
                    properties: PropTypes.array,
                }),
            }),
        }),

        insuranceImageUrl: PropTypes.string,
        intl: PropTypes.object,
        isProduction: PropTypes.bool,
        isSponsoredResult: PropTypes.bool,
        sponsoredData: PropTypes.shape({
            adDecisionId: PropTypes.number,
            adDecisionToken: PropTypes.string,
            adImpressionUrl: PropTypes.string,
        }),

        location: PropTypes.shape({
            address1: PropTypes.string,
            address2: PropTypes.string,
            address3: PropTypes.string,
            city: PropTypes.string,
            name: PropTypes.string,
            state: PropTypes.string,
            zipCode: PropTypes.string,
        }).isRequired,

        locationId: PropTypes.string.isRequired,

        provider: PropTypes.shape({
            approvedFullName: PropTypes.string,
            averageRating: PropTypes.number,
            circlePictureUrl: PropTypes.string,
            firstName: PropTypes.string,
            isPreview: PropTypes.bool,
            lastName: PropTypes.string,
            postnominal: PropTypes.string,
            prenominal: PropTypes.string,
            profileUrl: PropTypes.string,

            relevantSpecialty: PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string,
            }).isRequired,

            reviewCount: PropTypes.number,

            representativeReview: PropTypes.shape({
                comment: PropTypes.string,
            }),

            squarePictureUrl: PropTypes.string,
        }).isRequired,

        distance: PropTypes.number,

        providerId: PropTypes.string.isRequired,
        providerResultType: PropTypes.oneOf(Object.keys(PROVIDER_RESULT_TYPES)),
        searchResultId: PropTypes.string,
        sendMetric: PropTypes.func,
        shouldRenderReviewCount: PropTypes.bool,

    };

    static defaultProps = {
        isProduction: false,
        isSponsoredResult: false,
        shouldRenderReviewCount: false,
        sponsoredData: {},
        providerResultType: PROVIDER_RESULT_TYPES.IN_NETWORK,
        sendMetric: noop,
    };

    state = {
        mounted: false,
    };

    componentDidMount() {
        const { isSponsoredResult, trackConversion } = this.props;
        if (isSponsoredResult) {
            const { location } = window;

            trackConversion({
                conversionId: 'SPO_RecordImpressionEvent',
                sessionId: sanitizeId(cookie.get('bsid')),
                url: `${location.protocol}//${location.host}${location.pathname}`,
                userAgent: navigator.userAgent,
            });
        }

        this.setState({ mounted: true });
    }

    /*
    *  We only want to fire one click tracking event per doctor card click.
    *  When an inner element is clicked, set the click location on 'this'.
    *  The event will bubble up to the outer container, and fire the event using
    *  the click location from the inner element or the generic 'DoctorCard'
    *  click location.
    */
    setProviderLocationCardClickLocation = clickLocation => {
        this.doctorCardClickLocation = clickLocation;
    }

    trackProviderLocationCardClick = () => {
        const { searchResultId } = this.props;
        const clickLocation = this.doctorCardClickLocation || TRACKING_CONSTANTS.ProviderLocationCard;
        this.props.sendMetric({
            action: TRACKING_ACTIONS.ClickProviderLocationCard,
            element: clickLocation,
            eventDetails: {
                searchResultId,
            },
        });
        this.doctorCardClickLocation = null;
    }

    trackReviewsReadMoreToggle = () => {
        this.props.sendMetric({
            action: 'Toggle Read More/Less',
            element: 'ReviewReadMoreToggle',
        });
    }

    render() {
        const {
            data,
            distance,
            intl,
            isSponsoredResult,
            location,
            provider,
            providerResultType,
            showCommunityLabel,
            showInstitutes,
            sponsoredData,
            ...props
        } = this.props;

        const tier = getTier(data);

        const maybeUemId = isSponsoredResult ? { 'data-uem-id': 'sponsored-result-item' } : {};

        return (
            <DoctorProfileLink
                {...props}
                data-is-sponsored-result={isSponsoredResult}
                data-test="doctor-card"
                href={provider.profileUrl}
                onClick={this.trackProviderLocationCardClick}
                title={intl.formatMessage(LOCALIZED_STRINGS.ViewProfile, { name: provider.approvedFullName })}
                {...maybeUemId}
            >
                {this.maybeRenderTrackingPixel()}

                <PhotoContainer
                    data-test="doctor-card-photo"
                    onClick={() => this.setProviderLocationCardClickLocation(TRACKING_CONSTANTS.Photo)}
                >
                    <DoctorPhoto
                        isProduction={this.props.isProduction}
                        provider={provider}
                    />

                    {showCommunityLabel && isCommunity(provider) && (
                        <CommunityLabel data-test="community-label">
                            COMMUNITY
                        </CommunityLabel>
                    )}

                    {this.maybeRenderInsuranceImage({
                        imageUrl: props.insuranceImageUrl,
                        isPreview: provider.isPreview,
                        type: providerResultType,
                    })}
                    {this.maybeRenderOutOfNetworkMessage({
                        type: providerResultType,
                        isPreview: provider.isPreview,
                    })}
                    {this.maybeRenderRating(provider.averageRating, DoctorCardInfoRatingStarsTabletOnly)}
                </PhotoContainer>

                <DoctorInfoContainer data-test="doctor-card-info">
                    {isSponsoredResult && <SponsoredBanner />}

                    <DoctorInfoTopBar>
                        <DoctorName
                            data-test="doctor-card-info-name"
                            data-uem-id="doctor-card-info-name"
                            onClick={() => this.setProviderLocationCardClickLocation(TRACKING_CONSTANTS.Name)}
                        >
                            {provider.prenominal ? (
                                <span
                                    data-test="doctor-card-info-name-prefix"
                                    itemProp="honorificPrefix"
                                >
                                    {provider.prenominal}&nbsp;
                                </span>
                            ) : null}

                            <span
                                data-test="doctor-card-info-name-full"
                                itemProp="name"
                            >
                                {provider.firstName} {provider.lastName}
                            </span>

                            {provider.postnominal ? (
                                <span>
                                    ,&nbsp;
                                    <span
                                        data-test="doctor-card-info-name-suffix"
                                        itemProp="honorificSuffix"
                                    >
                                        {provider.postnominal}
                                    </span>
                                </span>
                            ) : null}
                        </DoctorName>

                        {this.maybeRenderDistance(distance)}
                    </DoctorInfoTopBar>

                    {
                        provider.relevantSpecialty && provider.relevantSpecialty.name && (
                            <DoctorSpecialty
                                data-test="doctor-card-info-specialty"
                                itemProp="jobTitle"
                            >
                                {provider.relevantSpecialty.name}
                            </DoctorSpecialty>
                        )
                    }

                    {showInstitutes && titlesFrom(provider) && (
                        <DoctorCardTitles
                            alt={titlesFrom(provider)}
                            data-test="doctor-card-titles"
                        >
                            {titlesFrom(provider)}
                        </DoctorCardTitles>
                    )}

                    <DoctorCardInfoRatingContainer
                        data-test="doctor-card-info-rating"
                        onClick={() => this.setProviderLocationCardClickLocation(TRACKING_CONSTANTS.Rating)}
                    >
                        {this.maybeRenderRating(provider.averageRating)}
                        {this.maybeRenderReviewCount(provider.reviewCount)}
                        {this.maybeRenderReview(provider.representativeReview)}
                    </DoctorCardInfoRatingContainer>

                    <DoctorCardInfoLocationContainer>
                        <DoctorCardInfoLocation
                            data-test="doctor-card-info-location"
                            itemProp="workLocation"
                            onClick={() => this.setProviderLocationCardClickLocation(TRACKING_CONSTANTS.Location)}
                        >
                            {this.maybeRenderFancyLocationName(location.name)}

                            <span
                                data-test="doctor-card-info-location-address"
                                itemProp="streetAddress"
                            >
                                {[
                                    location.address1,
                                    location.address2,
                                    location.address3,
                                ].filter(Boolean).join(', ')}
                            </span>

                            <span>
                                <span
                                    data-test="doctor-card-info-location-city"
                                    itemProp="addressLocality"
                                >
                                    {location.city}
                                </span>
                                <span
                                    data-test="doctor-card-info-location-state"
                                    itemProp="addressRegion"
                                >
                                    {location.state}
                                </span>
                                <span
                                    data-test="doctor-card-info-location-zip"
                                    itemProp="postalCode"
                                >
                                    {location.zipCode}
                                </span>
                            </span>
                        </DoctorCardInfoLocation>

                        {tier ? (
                            <NetworkLevel data-test="doctor-card-info-network-level">
                                Network level: {tier}
                            </NetworkLevel>
                        ) : null}
                    </DoctorCardInfoLocationContainer>
                </DoctorInfoContainer>
            </DoctorProfileLink>
        );
    }

    maybeRenderInsuranceImage({
        imageUrl,
        isPreview,
        type,
    }) {
        if (!isPreview && type === PROVIDER_RESULT_TYPES.IN_NETWORK && imageUrl) {
            return (
                <DoctorCardInsuranceImage
                    alt="Insurance logo"
                    data-test="doctor-card-insurance-image"
                    src={imageUrl}
                />
            );
        }
    }

    maybeRenderOutOfNetworkMessage({ type, isPreview }) {
        if (isPreview) {
            return (
                <DoctorCardInsuranceMessage data-test="doctor-card-provider-is-preview">
                    {this.props.intl.formatMessage(LOCALIZED_STRINGS.PreviewDoctorInsurance)}
                </DoctorCardInsuranceMessage>
            );
        } else if (type === PROVIDER_RESULT_TYPES.OUT_OF_NETWORK) {
            return (
                <DoctorCardInsuranceMessage data-test="doctor-card-provider-out-of-network">
                    {this.props.intl.formatMessage(LOCALIZED_STRINGS.OutOfNetwork)}
                </DoctorCardInsuranceMessage>
            );
        }
    }

    maybeRenderRating(rating, Component = DoctorCardInfoRatingStars) {
        if (rating > 0) {
            return (
                <Component
                    data-test="doctor-card-info-rating-stars"
                    rating={rating}
                />
            );
        }
    }

    maybeRenderReview(review) {
        if (review && review.comment) {
            const Component = this.state.mounted ? DoctorCardInfoRatingReviewCollapsible : DoctorCardInfoRatingReview;
            const content = review.comment.trim();

            return (
                <Component
                    data-test="doctor-card-info-rating-review"
                    maxLines={REVIEW_LINE_CLAMP}
                    onClickToggle={this.trackReviewsReadMoreToggle}
                >
                    {this.state.mounted ? content : <span>{content}</span>}
                </Component>
            );
        }
    }

    maybeRenderDistance(distance) {
        if (distance) {
            return (
                <DoctorCardInfoLocationDistance data-test="doctor-card-info-location-distance">
                    {this.props.intl.formatMessage(LOCALIZED_STRINGS.Distance, {
                        distance: roundToNearestHalf(distance),
                    })}
                </DoctorCardInfoLocationDistance>
            );
        }
    }

    maybeRenderFancyLocationName(name) {
        if (name) {
            return (
                <div
                    data-test="doctor-card-info-location-name"
                    itemProp="alternateName"
                >
                    {name}
                </div>
            );
        }
    }

    maybeRenderReviewCount(reviewCount) {
        const {
            isSponsoredResult,
            shouldRenderReviewCount,
            showReviewCount,
        } = this.props;
        const flapjackShowReviewCount = shouldRenderReviewCount && showReviewCount;
        if (reviewCount > 0 && (isSponsoredResult || flapjackShowReviewCount)) {
            return (
                <DoctorCardReviewCount data-test="doctor-card-review-count" >
                    {`(${this.props.intl.formatNumber(reviewCount)})`}
                </DoctorCardReviewCount>
            );
        }
    }

    maybeRenderTrackingPixel() {
        const {
            isSponsoredResult,
            sponsoredData,
        } = this.props;
        if (isSponsoredResult) {
            return (
                <Lazyload
                    data-test="lazyload-tracking-pixel"
                    shouldUnmountOnViewportExit={false}
                >
                    <TrackingPixel
                        data-test="spo-tracking-pixel"
                        src={sponsoredData.adImpressionUrl}
                    />
                </Lazyload>
            );
        }
    }
}

const DoctorProfileLink = DoctorCardContainer.withComponent('a');

const DoctorCardInsuranceImage = styled.img`
    display: none;

    ${breakpoint.medium} {
        display: block;
        height: auto;
        margin: 18px auto 0;
        max-height: 25px;
        max-width: 50px;
        width: auto;
    }
`;

const DoctorCardInsuranceMessage = styled.div`
    color: ${navy};
    display: none;
    font: 12px/1.4 ${FontFamily.SharpSansSemibold};
    margin-top: 22px;
    text-align: center;

    ${breakpoint.medium} {
        display: block;
    }
`;

const DoctorCardInfoLocationDistance = styled.div`
    color: ${grey};
    display: block;
    font-family: ${FontFamily.SharpSansSemibold};
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 4px;
    margin-left: 8px;

    ${breakpoint.medium} {
        line-height: 20px;
    }
`;

const DoctorCardTitles = styled(DoctorSpecialty)`
    line-height: 1.2em;
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DoctorCardInfoRatingContainer = styled.div`
    margin-top: 12px;

    @media (min-width: ${Breakpoints.small}px) and (max-width: ${Breakpoints.medium - 1}px) {
        display: none;
    }
`;

const DoctorCardInfoRatingStars = styled(Stars)`
    font-size: ${STARS_BASE_SIZE}px;
    vertical-align: middle;

    ${breakpoint.medium} {
        font-size: ${STARS_MEDIUM_SIZE}px;
    }

    ${breakpoint('small', { only: true })} {
        position: absolute;
        bottom: -39px;
        left: 0;
    }
`;

const DoctorCardInfoRatingStarsTabletOnly = styled(DoctorCardInfoRatingStars)`
    display: none !important;
    font-size: 12px !important;
    margin-top: 20px;

    @media (min-width: ${Breakpoints.small}px) and (max-width: ${Breakpoints.medium - 1}px) {
        display: block !important;
    }
`;

const DoctorCardReviewCount = styled.div`
    color: ${grey};
    display: inline-block;
    font: 12px ${FontFamily.SharpSansSemibold};
    line-height: 12px;
    margin-left: 5px;
`;

const REVIEW_LINE_CLAMP = 2;
const REVIEW_LINE_HEIGHT = INFO_TEXT_LINE_HEIGHT;

const reviewStyles = css`
    color: ${navy};
    display: block;
    font-family: ${FontFamily.KievitSlabBook};
    font-size: ${INFO_TEXT_FONT_SIZE}px;
    line-height: ${INFO_TEXT_LINE_HEIGHT}px;
    margin-top: 6px;
    padding: 0;

    span::before {
        content: '\u201C';
    }

    span::after {
        content: '\u201D';
    }

    ${breakpoint('medium', { down: true })} {
        display: none;
    }
`;

const DoctorCardInfoRatingReviewCollapsible = styled(CollapsibleTextByLine)`
    ${reviewStyles};
`;

const DoctorCardInfoRatingReview = styled.div`
    ${reviewStyles};
    max-height: ${REVIEW_LINE_HEIGHT * REVIEW_LINE_CLAMP}px;
    overflow: hidden;
`;

const DoctorCardInfoLocationContainer = styled.div`
    span:not(:last-of-type)::after {
        content: ', ';
    }

    color: ${navy};
    font: 13px/18px ${FontFamily.SharpSansSemibold};
    margin-top: 18px;

    @media (min-width: ${Breakpoints.xsmall}px) and (max-width: ${Breakpoints.medium - 1}px) {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        margin-top: 8px;
    }

    ${breakpoint.medium} {
        font-size: 14px;
        line-height: 20px;
    }

    ${breakpoint.small} {
        color: ${grey}
    }
`;

const DoctorCardInfoLocation = styled.div`
    margin-right: 10px;
`;

const NetworkLevel = styled.div`
    color: ${navy};
`;

const CommunityLabel = styled.div`
    color: ${navy};
    font: 10px ${FontFamily.SharpSansBold};
    margin-top: 4px;
    text-align: center;
`;

function roundToNearestHalf(num) {
    return Math.round(num * 2) / 2;
}

export default (
    injectIntl(
        BaseDoctorCard
    )
);
